import { createLocalVideoTrack, createLocalAudioTrack } from 'twilio-video';

import { createIntl, createIntlCache } from 'react-intl';
import { reportTwilioError } from './videoErrorReporting';
import French from '../../../lang/fr.json';
import English from '../../../lang/en.json';

const intl = () => {
  const cache = createIntlCache();
  const locale = localStorage.getItem('locale') || 'en';
  const messages = locale === 'en' ? English : French;

  return createIntl({ locale, messages }, cache);
};

const { formatMessage } = intl();

export const trackSubscribed = (div, track) => {
  // Data tracks (and other non-media tracks) do not implement attach()
  if (typeof track.attach !== 'function') return;
  div.appendChild(track.attach());
};

export const trackUnsubscribed = track => {
  if (typeof track.detach !== 'function') return;
  track.detach().forEach(element => element.remove());
};

export const participantConnected = participant => {
  const div = document.createElement('div');
  div.id = participant.sid;

  div.style.width = '100%';
  div.style.height = '100%';

  participant.on('trackSubscribed', track => trackSubscribed(div, track));
  participant.on('trackUnsubscribed', track => trackUnsubscribed(track));

  // Attach tracks that were already subscribed before these listeners were
  // registered, otherwise they would never be rendered (black screen/no audio)
  participant.tracks.forEach(publication => {
    if (publication.isSubscribed) {
      trackSubscribed(div, publication.track);
    }
  });

  const mediaDiv = document.getElementById('remote-media-div');
  const providerMedia = document.getElementById('remote-provider-media');
  const hostMedia = document.getElementById('remote-host-media');
  const guestMedia = document.getElementById('remote-guest-media');
  const partnerMedia = document.getElementById('remote-partner-media');
  const remoteCouplesDiv = document.getElementById('remote-couples');

  if (!participant.identity.includes('voice')) {
    if (participant.identity.includes('provider')) {
      (providerMedia || mediaDiv).appendChild(div);
    } else if (participant.identity.includes('host')) {
      if (participant.identity.includes('together')) {
        if (remoteCouplesDiv) remoteCouplesDiv.style.display = 'none';
        mediaDiv.appendChild(div);
      } else {
        if (remoteCouplesDiv) remoteCouplesDiv.style.display = 'flex';
        (hostMedia || partnerMedia || mediaDiv).appendChild(div);
      }
    } else if (participant.identity.includes('guest')) {
      if (remoteCouplesDiv) remoteCouplesDiv.style.display = 'flex';
      (guestMedia || partnerMedia || mediaDiv).appendChild(div);
    } else {
      mediaDiv.appendChild(div);
    }
  }
};

export const participantDisconnected = participant => {
  // Voice participants never have their div appended to the DOM
  const div = document.getElementById(participant.sid);
  if (div) div.remove();
};

// Participant identities follow a `prefix-role-...` convention, so role checks
// were written as a bare `identity.split('-')[1]`. That throws if identity is
// ever missing and silently misbehaves on unexpected formats. These guards
// return the same result for well-formed identities while tolerating
// null/odd values.
export function getIdentitySegments(identity) {
  if (typeof identity !== 'string') return [];
  return identity.split('-');
}

export function getIdentityRole(identity) {
  return getIdentitySegments(identity)[1] || '';
}

export function participantIsVoice(room) {
  // Any voice participant should flip the flag; iterating with per-participant
  // setState made the result depend on iteration order (last-writer-wins),
  // so a voice participant could be masked by a later video one in couples
  // sessions. Reduce over all participants instead.
  const hasVoiceParticipant = Array.from(room.participants.values()).some(
    participant => participant.identity.includes('voice'),
  );
  this.setState({ participantIsVoice: hasVoiceParticipant });
}

// Twilio fires `disconnected` with this error code when the access token
// backing the room expires. The Video JS SDK has no in-place token refresh, so
// the only recovery path is to fetch a fresh token and reconnect.
export const ACCESS_TOKEN_EXPIRED_CODE = 20104;

export function handleTokenExpiry(room, onTokenExpired) {
  room.on('disconnected', (disconnectedRoom, error) => {
    if (error && error.code === ACCESS_TOKEN_EXPIRED_CODE) {
      onTokenExpired(error);
    }
  });
}

// Twilio briefly emits `reconnecting` when a media/signalling connection drops
// (e.g. a short network blip) and `reconnected` once it recovers on its own.
// Surfacing these lets the screens show a non-blocking indicator instead of a
// silently frozen video.
export function handleReconnection(
  room,
  onReconnecting,
  onReconnected,
  onDisconnected,
) {
  room.on('reconnecting', () => onReconnecting());
  room.on('reconnected', () => onReconnected());
  // If reconnection ultimately fails, Twilio emits `disconnected` (it gives up)
  // instead of `reconnected`. Always clear the indicator so it does not stay
  // stuck showing "reconnecting" on a call that is no longer recovering. For an
  // unexpected drop (an error that is not the self-recovering token-expiry
  // case), also surface a terminal "connection lost" state so the user isn't
  // left staring at frozen video with no explanation. A normal leave emits
  // `disconnected` with no error, so it only clears the indicator.
  room.on('disconnected', (disconnectedRoom, error) => {
    onReconnected();
    if (error && error.code !== ACCESS_TOKEN_EXPIRED_CODE && onDisconnected) {
      onDisconnected(error);
    }
  });
}

// all the twilio listeners
export function teardownRoom(room) {
  // Cleanly tear down a room on unmount: stop the local camera/mic tracks (so
  // the hardware indicator light turns off and the devices are released) and
  // then disconnect. This replaces a blunt `window.location.reload()` that
  // previously forced a full page reload purely to release media.
  if (!room) return;
  if (room.localParticipant && room.localParticipant.tracks) {
    room.localParticipant.tracks.forEach(publication => {
      const { track } = publication;
      if (track && typeof track.stop === 'function') {
        track.stop();
      }
    });
  }
  if (typeof room.disconnect === 'function') {
    room.disconnect();
  }
}

export function initRoom(room) {
  this.setState({ room }, () => {
    createLocalVideoTrack().then(localVideoTrack => {
      room.localParticipant.publishTrack(localVideoTrack);
      const localMediaContainer = document.getElementById('local-media');
      localMediaContainer.appendChild(localVideoTrack.attach());
      this.setState({ localVideoTrackId: localVideoTrack.id });
    }, handleHardwareError.bind(this));

    createLocalAudioTrack().then(localAudioTrack => {
      room.localParticipant.publishTrack(localAudioTrack);
      this.setState({ localAudioTrackId: localAudioTrack.id });
    }, handleHardwareError.bind(this));

    this.state.room.on('participantConnected', () => {
      participantIsVoice.bind(this)(this.state.room);
    });

    this.state.room.participants.forEach(participantConnected);
    this.state.room.on('participantConnected', participantConnected);

    this.state.room.on('participantDisconnected', participantDisconnected);

    // Attach the Participant's Media to a <div> element.
    this.state.room.once('disconnected', () =>
      room.participants.forEach(participantDisconnected),
    );

    // on close browser we disconnect them
    window.addEventListener('beforeunload', () => {
      this.state.room.disconnect();
    });
  });
}
export function toggleCamera() {
  const { room } = this.state;
  if (!room) return;
  const { localParticipant } = room;

  // Re-entrancy guard: enabling the camera is asynchronous, so rapid repeat
  // clicks would otherwise each read the pre-toggle state, both take the
  // enable branch, and publish/attach a second track — rendering duplicate
  // local video tiles. Ignore clicks while an enable is still in flight.
  if (this.isTogglingCamera) return;

  const nextCameraOpen = !this.state.cameraOpen;
  this.setState({ cameraOpen: nextCameraOpen });

  if (nextCameraOpen) {
    this.isTogglingCamera = true;
    createLocalVideoTrack().then(
      track => {
        const localMediaContainer = document.getElementById('local-media');
        if (localMediaContainer) {
          // Clear any existing local video so we never stack duplicate tiles.
          localMediaContainer.innerHTML = '';
          localMediaContainer.appendChild(track.attach());
        }
        localParticipant.publishTrack(track);
        this.isTogglingCamera = false;
      },
      error => {
        this.isTogglingCamera = false;
        handleHardwareError.bind(this)(error);
      },
    );
  } else {
    const track = localParticipant.videoTracks.values().next().value;
    // Guard against no published video track (camera already off or
    // unavailable); closing the camera is a safe no-op then (Sentry 5C9/4TQ)
    if (!track || !track.track) return;
    track.track.stop();
    track.track.detach().forEach(element => element.remove());
    localParticipant.unpublishTrack(track.track);
  }
}

export function toggleMic() {
  const { room } = this.state;
  const { localParticipant } = room;
  if (room) {
    this.setState(prevState => ({
      audioOpen: !prevState.audioOpen,
    }));
    if (!this.state.audioOpen) {
      localParticipant.audioTracks.forEach(audioTrack => {
        audioTrack.track.enable();
      });
    } else {
      localParticipant.audioTracks.forEach(audioTrack => {
        audioTrack.track.disable();
      });
    }
  }
}

export const networkColor = level => {
  if (level < 2) {
    // red
    return '#B81D13';
  }
  if (level < 4) {
    // yellow
    return '#EFB700';
  }
  // green
  return '#008450';
};

export const networkQuality = {
  1: '▃',
  2: '▃▄',
  3: '▃▄▅',
  4: '▃▄▅▆',
  5: '▃▄▅▆▇',
};

export function printNetworkQualityStats(
  networkQualityLevel,
  networkQualityStats,
) {
  // Print in console the networkQualityLevel using bars

  this.setState({ networkQualityLevel });
  if (networkQualityStats) {
    // Print in console the networkQualityStats, which is non-null only if Network Quality
    // verbosity is 2 (moderate) or greater
  }
}

// Maps a getUserMedia / createLocalTrack rejection (a DOMException-style error
// whose `name` describes the cause) to a localized, actionable message and
// stores it in the `hardwareError` / `hardwareErrorCode` state that both video
// screens already render. Centralizing this lets detectHardware AND the local
// track creation in initRoom/toggleCamera surface the same guidance instead of
// failing as silent unhandled promise rejections (POD2-1958).
export function handleHardwareError(error) {
  const { name } = error || {};
  if (name === 'NotAllowedError') {
    this.setState({
      hardwareError: formatMessage({
        defaultMessage:
          'Your browser is not permitting access to your camera/microphone, please refresh after fixing.',
      }),
      hardwareErrorCode: name,
    });
  } else if (name === 'NotFoundError') {
    this.setState({
      hardwareError: formatMessage({
        defaultMessage:
          'Unable to detect a camera/microphone, please refresh after fixing.',
      }),
      hardwareErrorCode: name,
    });
  } else if (name === 'NotReadableError') {
    this.setState({
      hardwareError: formatMessage({
        defaultMessage:
          'A hardware issue has occurred with your camera/microphone, please refresh after fixing.',
      }),
      hardwareErrorCode: name,
    });
  } else {
    this.setState({
      hardwareError: formatMessage({
        defaultMessage: 'Unknown Error',
      }),
      hardwareErrorCode: 'UnknownError',
    });
  }
}

export function handleConnectionError(error) {
  reportTwilioError(error, 'connect');

  // Use a dedicated field instead of hardwareError: detectHardware() runs in
  // parallel on mount and its success handler resets hardwareError, which would
  // otherwise wipe this message when the connection fails offline.
  this.setState({
    connectionError: formatMessage({
      defaultMessage:
        'We were unable to connect you to the session. Please refresh the page to try again.',
    }),
    connectionErrorCode: (error && error.name) || 'ConnectionError',
  });
}

export function detectHardware() {
  const constraints = { video: true, audio: true };
  navigator.mediaDevices
    .getUserMedia(constraints)
    .then(stream => {
      // Release the probe stream immediately so the camera/mic indicator
      // doesn't stay lit; Twilio acquires its own tracks for the call.
      stream.getTracks().forEach(track => track.stop());
      this.setState({
        hardwareError: '',
        hardwareErrorCode: '',
      });
    })
    .catch(handleHardwareError.bind(this));
}
